/**
 * Iter 100 — Patient-facing Education Library.
 *
 * Renders the patient's queue of assigned education videos. Tab between
 * "To watch" and "Completed". Click → opens an in-page iframe player +
 * "I watched this" button that marks completion.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import {
  BookOpen, CheckCircle2, PlayCircle, Clock, Tag,
} from 'lucide-react';

export default function PatientEducation() {
  const [assignments, setAssignments] = useState([]);
  const [tab, setTab] = useState('queue');   // queue | done
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get('/education/me/assignments');
    setAssignments(data.assignments || []);
  };
  useEffect(() => { load(); }, []);

  const markViewed = async (a) => {
    setBusy(true);
    try {
      await api.post(`/education/assignments/${a.assignment_id}/viewed`);
      setOpen(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const queue = assignments.filter(a => !a.completed_at);
  const done = assignments.filter(a => a.completed_at);
  const visible = tab === 'queue' ? queue : done;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="patient-education-page">
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2 inline-flex items-center gap-1">
        <BookOpen className="w-3 h-3"/> YOUR EDUCATION LIBRARY
      </div>
      <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Watch &amp; learn</h1>
      <p className="text-fg-muted text-sm mt-1 mb-6">
        Your clinician has put together these short videos to help with your recovery. Watch each one and check it off when you're done.
      </p>

      {/* Tab strip */}
      <div className="flex gap-2 border-b border-soft mb-5" data-testid="edu-tabs">
        <button onClick={() => setTab('queue')}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition ${tab === 'queue' ? 'border-brand text-brand font-semibold' : 'border-transparent text-fg-muted'}`}
                data-testid="edu-tab-queue">
          To watch ({queue.length})
        </button>
        <button onClick={() => setTab('done')}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition ${tab === 'done' ? 'border-brand text-brand font-semibold' : 'border-transparent text-fg-muted'}`}
                data-testid="edu-tab-done">
          Completed ({done.length})
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="surface border border-soft rounded-xl p-10 text-center" data-testid="edu-empty">
          <BookOpen className="w-12 h-12 text-fg-muted/40 mx-auto mb-3"/>
          <p className="font-semibold mb-1">
            {tab === 'queue' ? "You're all caught up!" : 'No completed videos yet.'}
          </p>
          <p className="text-sm text-fg-muted">
            {tab === 'queue'
              ? 'Your clinician will share videos here when they assign new ones.'
              : 'Videos you watch will show up here.'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="edu-list">
          {visible.map(a => (
            <button key={a.assignment_id}
                    onClick={() => setOpen(a)}
                    className="surface border border-soft rounded-xl p-4 hover:border-brand transition text-left"
                    data-testid={`edu-item-${a.assignment_id}`}>
              <div className="aspect-video bg-bg-secondary rounded mb-3 flex items-center justify-center overflow-hidden relative">
                {a.thumbnail_url
                  ? <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                  : <PlayCircle className="w-12 h-12 text-fg-muted/30"/>}
                {a.completed_at && (
                  <div className="absolute top-2 right-2 bg-[#1B5E20] text-white rounded-full p-1">
                    <CheckCircle2 className="w-3 h-3"/>
                  </div>
                )}
              </div>
              <h3 className="font-heading font-semibold text-sm leading-tight mb-1">{a.title}</h3>
              <div className="flex items-center gap-2 text-[10px] text-fg-muted">
                <span className="inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5"/>{a.category}</span>
                {a.duration_sec && <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5"/>{Math.floor(a.duration_sec / 60)}m</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Player modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}
              data-testid="edu-player-modal">
          <div className="bg-bg-secondary rounded-xl p-5 max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading font-bold mb-2">{open.title}</h2>
            {open.description && <p className="text-sm text-fg-muted mb-3">{open.description}</p>}
            <div className="aspect-video bg-black rounded mb-3 overflow-hidden">
              <EducationPlayer url={open.video_url}/>
            </div>
            <div className="flex justify-between items-center gap-2">
              <button onClick={() => setOpen(null)} className="btn-secondary">Close</button>
              {!open.completed_at ? (
                <button onClick={() => markViewed(open)} disabled={busy}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="edu-mark-watched">
                  <CheckCircle2 className="w-4 h-4"/> {busy ? 'Saving…' : "I watched this"}
                </button>
              ) : (
                <span className="text-sm text-[#1B5E20] inline-flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="w-4 h-4"/> Completed {new Date(open.completed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EducationPlayer({ url }) {
  // Naive embed support — YouTube, Vimeo, or direct video file.
  const yt = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) {
    return <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`}
                    title="" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen/>;
  }
  const vimeo = url?.match(/vimeo\.com\/(\d+)/);
  if (vimeo) {
    return <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${vimeo[1]}`}
                    title="" allow="autoplay; fullscreen" allowFullScreen/>;
  }
  return <video className="w-full h-full" src={url} controls autoPlay/>;
}
